/**
 * Outdoor Rectangular Orchestrator
 *
 * Coordinates rendering of flat outdoor areas (ramp, tiedown).
 * Renders optional semi-transparent floor + border lines.
 * No walls, no doors.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'
import { renderFloor } from '../primitives/FloorRenderer'
import { renderBorder } from '../primitives/BorderRenderer'
import { createFloorMaterial } from '../utils/materials'

/**
 * Outdoor area render options
 */
export interface OutdoorRenderOptions {
  buildingId?: string
  buildingName?: string
  position?: [number, number, number]
  rotationDeg?: number
  enableShadows?: boolean
  // These are read from building.settings but can be overridden
  floorColor?: string
  wallColor?: string
  wallThickness?: number
}

/**
 * Render an outdoor rectangular area from generated config
 */
export function renderOutdoorRectangularBuilding(
  building: GeneratedBuilding,
  _units: GeneratedUnit[],
  options: OutdoorRenderOptions = {}
): THREE.Group {
  const {
    buildingId,
    buildingName,
    position = [0, 0, 0],
    rotationDeg,
    enableShadows = true,
  } = options

  const group = new THREE.Group()
  group.name = `building-${buildingId || building.code}`
  group.userData = {
    buildingId: buildingId || building.code,
    buildingName: buildingName || building.name,
    buildingType: building.type,
    template: 'outdoor_rectangular',
  }

  // Read visual config from building settings
  const settings = building.settings || {}
  const borderColor = settings.border_color || '#FFD700'
  const borderStyle = settings.border_style || 'solid'
  const borderWidthFt = settings.border_width_ft ?? 2
  const floorColor = settings.floor_color
  const floorOpacity = settings.floor_opacity ?? 0.3

  const width = building.exterior_width
  const depth = building.exterior_depth

  // Render floor if floor_color is set (non-null)
  if (floorColor) {
    // Clone material (don't cache, since opacity varies per-area)
    const floorMat = createFloorMaterial({
      color: floorColor,
      opacity: floorOpacity,
      transparent: true,
    }).clone()
    floorMat.opacity = floorOpacity
    floorMat.transparent = true

    const floor = renderFloor(
      { width, depth },
      { color: floorColor, enableShadows, yOffset: 0.02 }
    )
    // Replace the cached material with our custom-opacity one
    floor.material = floorMat
    group.add(floor)
  }

  // Render border
  const border = renderBorder(
    { width, depth },
    { color: borderColor, style: borderStyle as 'solid' | 'dashed', widthFt: borderWidthFt }
  )
  group.add(border)

  // Apply building rotation
  const rotation = rotationDeg ?? (building.rotation * 180 / Math.PI)
  if (rotation !== 0) {
    group.rotation.y = (rotation * Math.PI) / 180
  }

  // Apply building position (convert feet to meters)
  group.position.set(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  return group
}
